import { BlockEmbed } from 'quill/blots/block';
import React from 'react';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import DeleteMenuItem from './DeleteMenuItem';
import FloatingToolbar from './FloatingToolbar';
import Toolbar, { ToolbarOverflowMenu } from './Toolbar';
import { useAppDispatch } from '../../../app/redux/useAppSelector';
import { MenuSeparator } from '../../../components/Menu';
import newTabIcon from '../../../icons/non-prefilled/16x16/newTab.svg';
import { UIActions } from '../../../ui/uiSlice';
import fullOrgUrl from '../../../utils/url/fullOrgUrl';
import postPath from '../../../utils/url/postPath';
import useIsPublicView from '../../../utils/useIsPublicView';
import type { CardValue } from '../../formats/Card';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import ConvertIntoEmbedMenuItem from '../ConvertIntoEmbedMenuItem';
import ConvertIntoLinkMenuItem from '../ConvertIntoLinkMenuItem';
import ConvertIntoMentionMenuItem from '../ConvertIntoMentionMenuItem';
import CopyLinkMenuItem from '../CopyLinkMenuItem';
import { copyIcon } from '../editorIcons';
import OpenInNewTabMenuItem from '../OpenInNewTabMenuItem';

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

type Value = { card: CardValue };

interface CardToolbarOverflowMenuItemsProps {
  fullUrl: string;
  blot: BlockEmbed;
  value: Value;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const CardToolbarOverflowMenuItems = ({
  fullUrl,
  blot,
  value,
}: CardToolbarOverflowMenuItemsProps) => {
  const { quill } = useQuill();
  return (
    <>
      <OpenInNewTabMenuItem link={fullUrl} />
      {quill && (
        <>
          <ConvertIntoMentionMenuItem
            quill={quill}
            blot={blot}
            type="post"
            id={value.card.id}
            params={value.card.params}
          />
          <ConvertIntoEmbedMenuItem quill={quill} blot={blot} />
          {fullUrl && (
            <ConvertIntoLinkMenuItem quill={quill} blot={blot} link={fullUrl} />
          )}
        </>
      )}
      <CopyLinkMenuItem link={fullUrl} />
      <MenuSeparator />
      <DeleteMenuItem onClick={() => blot.remove()}>Delete card</DeleteMenuItem>
    </>
  );
};

interface BaseCardToolbarProps {
  hoveredBlot: BlockEmbed;
  formats: Formats;
  setPersist: (persist: boolean) => void;
  value: Value;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseCardToolbar = ({
  hoveredBlot,
  formats,
  setPersist,
  value,
}: BaseCardToolbarProps) => {
  const isPublicView = useIsPublicView();
  const dispatch = useAppDispatch();
  const { readOnly } = useQuill();
  const fullUrl = fullOrgUrl(
    postPath({
      id: value.card.id,
      title: '',
    }),
  );

  if (readOnly && isPublicView) {
    return (
      <Toolbar>
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={newTabIcon}
            tooltip="Open in new tab"
            to={fullUrl}
            newTab
          >
            Open
          </ToolbarButton>
        </ToolbarButtonGroup>
      </Toolbar>
    );
  }

  return (
    <Toolbar
      targetBlot={hoveredBlot}
      overflowMenu={
        readOnly ? undefined : (
          <ToolbarOverflowMenu onMenuOpenChange={setPersist}>
            <CardToolbarOverflowMenuItems
              value={value}
              blot={hoveredBlot}
              fullUrl={fullUrl}
            />
          </ToolbarOverflowMenu>
        )
      }
    >
      {readOnly && fullUrl && (
        <>
          <ToolbarButtonGroup>
            <ToolbarButton
              icon={newTabIcon}
              tooltip="Open in new tab"
              to={fullUrl}
              newTab
            />
          </ToolbarButtonGroup>
          <ToolbarButtonGroup>
            <ToolbarButton
              tooltip="Copy link"
              icon={copyIcon}
              onClick={async () => {
                await navigator.clipboard.writeText(fullUrl);
                dispatch(
                  UIActions.pushToast({
                    level: 'success',
                    message: 'Link copied',
                  }),
                );
              }}
            />
          </ToolbarButtonGroup>
        </>
      )}

      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
    </Toolbar>
  );
};

type Context = {
  formats: Record<string, string[] | string | null | undefined>;
  value: Value;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  isHoveredBlot(blot) {
    return blot.statics.blotName === 'card';
  },
  getContext(blot: BlockEmbed) {
    return {
      formats: blot.formats(),
      value: blot.value(),
    };
  },
};

const CardToolbar = () => {
  const { quill } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { formats, value } = { formats: undefined, value: undefined },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  return (
    <FloatingToolbar.Toolbar bounds={bounds}>
      {hoveredBlot instanceof BlockEmbed && value && formats && (
        <BaseCardToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          setPersist={setPersist}
          value={value}
        />
      )}
    </FloatingToolbar.Toolbar>
  );
};

export default CardToolbar;
